// Cash collected target: one shared monthly anchor plus optional per-week overrides (Joey Forslund, Oct 19, 2026,
// 10:36 PM ET: "if my sales manager ... wants to structure the target metric for that week to be $25,000, then it will
// be the basis for our target metrics"). Stored server-side on GET/PUT /api/booked-call-targets (monthlyTarget +
// weeklyTargets, keyed by the week's Monday, Eastern). A week with an override is scored as if the monthly goal were
// override × 4.33, so its weekly cash target is exactly the override and every other target (revenue = cash ÷ 75%,
// volumes, net cash, closed deals) derives from it the same way it derives from the monthly goal. Weeks without an
// override use the monthly anchor. Pure: used by the Velocity sheet, cards, goal panel and the Book a call sheet.
import { WEEKS_PER_MONTH } from "../config/funnel-targets";

/** Monday (YYYY-MM-DD, Eastern week) → weekly cash collected target in dollars. */
export type WeeklyCashTargets = Record<string, number>;

const DAY = 86_400_000;
const valid = (value: string | undefined | null): value is string => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
const parse = (value: string) => Date.UTC(Number(value.slice(0, 4)), Number(value.slice(5, 7)) - 1, Number(value.slice(8, 10)));
const fmt = (time: number) => new Date(time).toISOString().slice(0, 10);

export function todayEastern() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}
/** The Monday of the Mon–Sun week holding `date`. */
export function weekStartOf(date: string) { const time = parse(date); return fmt(time - ((new Date(time).getUTCDay() + 6) % 7) * DAY); }
export const weekEndOf = (weekStart: string) => fmt(parse(weekStart) + 6 * DAY);
export const addWeeks = (weekStart: string, weeks: number) => fmt(parse(weekStart) + weeks * 7 * DAY);
export const currentWeekStartEastern = () => weekStartOf(todayEastern());
export const isMonday = (value: string) => valid(value) && new Date(parse(value)).getUTCDay() === 1;
export function weekLabel(weekStart: string) {
  const label = (value: string) => new Date(parse(value)).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  return `${label(weekStart)}–${label(weekEndOf(weekStart))}`;
}

/** The monthly-goal equivalent of a weekly cash target (weekly × 4.33). */
export const monthlyGoalForWeek = (weeklyCash: number) => weeklyCash * WEEKS_PER_MONTH;
/** The weekly cash target the monthly goal gives (goal ÷ 4.33). */
export const weeklyCashOfMonthly = (monthlyGoal: number) => monthlyGoal / WEEKS_PER_MONTH;

/** Cleans a server payload into { Monday: dollars }. */
export function normalizeWeeklyTargets(raw: unknown): WeeklyCashTargets {
  const out: WeeklyCashTargets = {};
  if (!raw || typeof raw !== "object") return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) { const n = Number(value); if (isMonday(key) && Number.isFinite(n) && n > 0) out[key] = n; }
  return out;
}

/**
 * The goal one column is scored against, as a monthly-goal equivalent. Days in a week with an override count at
 * override × 4.33, other days at the monthly goal; a column covering several weeks takes the day-weighted mean, which
 * gives exactly the sum of each day's target because every volume target is linear in days and in the goal.
 */
export function columnGoal(startDate: string | undefined, endDate: string | undefined, monthlyGoal: number, weekly?: WeeklyCashTargets | null) {
  if (!weekly || !Object.keys(weekly).length || !valid(startDate) || !valid(endDate)) return monthlyGoal;
  let total = 0; let count = 0;
  for (let time = parse(startDate); time <= parse(endDate) && count < 3_700; time += DAY) {
    const override = weekly[weekStartOf(fmt(time))];
    total += override && override > 0 ? monthlyGoalForWeek(override) : monthlyGoal; count += 1;
  }
  return count ? total / count : monthlyGoal;
}

/** Overrides that touch [startDate, endDate], oldest first. */
export function overridesInRange(startDate: string | undefined, endDate: string | undefined, weekly?: WeeklyCashTargets | null): Array<{ weekStart: string; weekly: number }> {
  if (!weekly || !valid(startDate) || !valid(endDate)) return [];
  const first = weekStartOf(startDate);
  return Object.entries(weekly).filter(([weekStart, value]) => value > 0 && weekStart >= first && weekStart <= endDate).sort(([a], [b]) => a.localeCompare(b)).map(([weekStart, value]) => ({ weekStart, weekly: value }));
}
